import React, { useEffect, useRef, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { io } from "socket.io-client";
import ChatHistoryServices from "services/ChatHistoryServices";
import formatDuration from "utils/formatDuration";

// Chats still in progress are followed live over the socket.
const LIVE_STATUSES = ["active", "transferred"];

// Merge by message id (ids only grow), so a message that arrives live while
// the history is still loading is neither lost nor shown twice.
const mergeMessages = (a, b) => {
  const byId = new Map();
  [...a, ...b].forEach((m) => byId.set(String(m.id), m));
  return [...byId.values()].sort((x, y) => Number(x.id) - Number(y.id));
};

// Socket timestamps come as "YYYY-MM-DD HH:MM:SS.ffffff+TZ" — show them like
// the history rows ("DD-MM-YYYY HH:MM:SS").
const formatLiveTime = (ts) => {
  const d = new Date(String(ts || "").replace(" ", "T"));
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// Right-side drawer that loads and shows one chat's full conversation, and
// keeps it updating live while the chat is still going on.
// `chat` is a row from the chat history list (chat_id, status, agent_name,
// employee_name, sub_agent, rating, comments, support_message, created_at).
const ChatMessagesDrawer = ({ chat, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [endedBy, setEndedBy] = useState(null);
  const bottomRef = useRef(null);

  const isOpen = Boolean(chat);
  // Following a chat live is for admins only; others just see its history.
  const viewerIsAdmin = (() => {
    const info = localStorage.getItem("adminInfo") ? JSON.parse(localStorage.getItem("adminInfo")) : null;
    return info?.id === 1 || String(info?.profile || "").toLowerCase() === "admin";
  })();
  const followLive =
    viewerIsAdmin && LIVE_STATUSES.includes(String(chat?.status || "").toLowerCase());

  useEffect(() => {
    if (!chat?.chat_id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMessages([]);
    ChatHistoryServices.getMessagesByChatId(chat.chat_id)
      .then((res) => {
        if (cancelled) return;
        if (res?.status) setMessages((prev) => mergeMessages(res.data || [], prev));
        // An active chat with no messages yet is fine — they'll arrive live.
        else if (res?.message !== "No record found") setError(res?.message || "Could not load messages.");
      })
      .catch(() => !cancelled && setError("Could not load messages."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [chat?.chat_id]);

  // Live updates: a separate, short-lived socket connection that never
  // registers — so opening a chat here never marks the viewer online or
  // available for customer chats.
  useEffect(() => {
    if (!chat?.chat_id || !followLive) return;
    const chatId = chat.chat_id;
    const adminInfo = localStorage.getItem("adminInfo")
      ? JSON.parse(localStorage.getItem("adminInfo"))
      : null;
    setIsLive(false);
    setEndedBy(null);

    const liveSocket = io(`${process.env.REACT_APP_API_SOCKET_URL}`, {
      transports: ["websocket"],
    });
    liveSocket.on("connect", () =>
      liveSocket.emit("watch_chat", { chatId, employeeId: adminInfo?.id })
    );
    liveSocket.on("watch_chat_ok", () => setIsLive(true));
    liveSocket.on("disconnect", () => setIsLive(false));
    liveSocket.on("watched_chat_message", (m) => {
      if (String(m.chatId) !== String(chatId) || m.id == null) return;
      setMessages((prev) =>
        mergeMessages(prev, [
          {
            id: m.id,
            message: m.message,
            type: m.type || "text",
            sender_id: m.senderId,
            from_agent: typeof m.fromAgent === "boolean" ? m.fromAgent : undefined,
            sender_name: m.senderName,
            receiver_name: m.receiverName,
            date_time: formatLiveTime(m.timestamp),
          },
        ])
      );
    });
    liveSocket.on("watched_chat_ended", (e) => {
      if (String(e.chatId) !== String(chatId)) return;
      setEndedBy(e.endedBy || "system");
      setIsLive(false);
    });

    return () => {
      liveSocket.emit("unwatch_chat", { chatId });
      liveSocket.disconnect();
    };
  }, [chat?.chat_id, followLive]);

  // Start at the latest message, like a chat window.
  useEffect(() => {
    if (messages.length) bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // The agent (customer) side sits on the left, staff (any executive, incl.
  // after a transfer) on the right. Decided by the chat's customer id — the
  // agent sends under a person's name (e.g. "Thilip"), not the company name.
  // The server marks each message (from_agent); the id/name checks are only
  // a fallback for older API responses.
  const isAgent = (msg) => {
    if (typeof msg.from_agent === "boolean") return msg.from_agent;
    if (chat?.customer_id != null) return String(msg.sender_id) === String(chat.customer_id);
    return Boolean(chat?.agent_name) && msg.sender_name === chat.agent_name;
  };

  return (
    <>
      {/* Dimmed backdrop — inline colour because Tailwind 1.9 has no
          bg-opacity-30 (the class fell back to solid black). */}
      <div
        className={`fixed inset-0 z-40 transition-colors duration-200 ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={{ backgroundColor: isOpen ? "rgba(0, 0, 0, 0.25)" : "rgba(0, 0, 0, 0)" }}
        onClick={onClose}
      />
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full bg-white dark:bg-gray-800 shadow-xl flex flex-col transform transition-transform duration-200 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ maxWidth: "620px" }}
        aria-hidden={!isOpen}
      >
        {chat && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0">
                <h2 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                  {chat.agent_name || "Agent"}
                  {chat.sub_agent ? ` · ${chat.sub_agent}` : ""}
                  {isLive && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      ● Live
                    </span>
                  )}
                  {endedBy && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                      Chat ended by {endedBy}
                    </span>
                  )}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Chat #{chat.ref_no || chat.chat_id} · {chat.employee_name || "-"} · {chat.created_at}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-red-600 focus:outline-none text-2xl leading-none"
                aria-label="Close"
              >
                <IoIosClose />
              </button>
            </div>

            {/* Chat details */}
            <div className="px-4 py-2 text-xs text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 space-y-1">
              {chat.support_message && (
                <div>
                  <span className="font-medium">Support message: </span>
                  {chat.support_message}
                </div>
              )}
              {chat.duration_seconds != null && (
                <div>
                  <span className="font-medium">Duration: </span>
                  {formatDuration(chat.duration_seconds, chat.status)}
                </div>
              )}
            </div>

            {/* Review for this chat (the agent's rating + comment) */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Review</div>
              {Number(chat.rating) > 0 ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-lg leading-none" aria-label={`${chat.rating} out of 5`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span key={n} className={n <= Math.round(Number(chat.rating)) ? "text-yellow-500" : "text-gray-300"}>
                          ★
                        </span>
                      ))}
                    </span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {Number(chat.rating)} / 5
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {chat.comments ? `“${chat.comments}”` : "No comment."}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-500">Not rated yet.</p>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-3 py-2 bg-gray-50 dark:bg-gray-900">
              {loading && (
                <p className="text-center text-sm text-gray-500 mt-6">Loading messages…</p>
              )}
              {!loading && error && (
                <p className="text-center text-sm text-gray-500 mt-6">{error}</p>
              )}
              {!loading && !error && messages.length === 0 && (
                <p className="text-center text-sm text-gray-500 mt-6">
                  {isLive ? "No messages yet — new ones will appear here." : "No messages in this chat."}
                </p>
              )}
              {!loading &&
                !error &&
                messages.map((msg) => {
                  const agent = isAgent(msg);
                  return (
                    <div
                      key={msg.id}
                      className={`p-1 my-1 flex flex-col ${agent ? "items-start" : "items-end"}`}
                    >
                      <div className={`text-xs mb-1 ${agent ? "text-gray-400" : "text-green-600"}`}>
                        {msg.sender_name}
                      </div>
                      <div
                        className={`rounded-lg p-2 break-words text-sm ${
                          agent ? "bg-gray-300 text-black" : "bg-green-500 text-white"
                        }`}
                        style={{ width: "fit-content", maxWidth: "80%" }}
                      >
                        {msg.type === "image" ? (
                          <a href={msg.message} target="_blank" rel="noopener noreferrer">
                            <img src={msg.message} alt="Attachment" className="rounded-lg max-w-full" style={{ maxHeight: 220 }} />
                          </a>
                        ) : msg.type === "pdf" ? (
                          <a href={msg.message} target="_blank" rel="noopener noreferrer" className="underline">
                            📄 Open PDF
                          </a>
                        ) : (
                          msg.message
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5" style={{ fontSize: 10 }}>
                        {msg.date_time}
                      </div>
                    </div>
                  );
                })}
              <div ref={bottomRef} />
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default ChatMessagesDrawer;
